import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import Layout from './Layout';
import './DeliveryScreen.css';
import withAuth from '../utils/withAuth';
import DeliveryForm from './DeliveryForm';
import { Link } from 'react-router-dom';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import DocumentPanel from './DocumentPanel';
import DocumentsSplitPane from './DocumentsSplitPane';
import ChecklistPanel from './ChecklistPanel';
import InvoicePanel from './InvoicePanel';
import FinanceCalculator from './FinanceCalculator/FinanceCalculator';
import ActionButton from './ActionButton';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faPaperPlane, faCheckCircle, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import DeliveryLogsModal from './Deliveries/DeliveryLogsModal';
import DeliveryBackModal from './DeliveryBackModal';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';
import { goToTradeOps, useTradeOpsDesk } from '../utils/tradeOpsDesk';

import PinToDeskButton from './mydesk/PinToDeskButton';
import CreatedByLine from './ui/CreatedByLine';
import HeaderNameEditor from './ui/HeaderNameEditor';
import { isOwnSubmit } from '../utils/formSubmit';

// "Failed to create N blank recap(s)", followed by the server's reason for the
// first one, so a refusal is read, not only counted.
const blankRecapFailure = (errors) => {
  const count = `Failed to create ${errors.length} blank recap(s)`;
  const reason = getErrorMessage(errors[0] && errors[0].error, '');
  return reason ? `${count}: ${reason}` : count;
};

function DeliveryScreen() {
  const { deliveryId } = useParams();
  const history = useHistory();
  const location = useLocation();
  // Trade Ops flavour carried on the router state by the Bunkers create path;
  // a deep link with no state is documented to fall back to solids. It only
  // seeds a NEW delivery's commodity_type; the desk (commodityType below) comes
  // from tradeOpsDesk once the record has loaded.
  const stateCommodityType = (location.state?.commodityType === 'fuels') ? 'fuels' : 'solids';
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  const formRef = useRef(null);
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState(false);
  const [isChecklistPanelOpen, setIsChecklistPanelOpen] = useState(false);
  const [isInvoicePanelOpen, setIsInvoicePanelOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isFinanceCalculatorExpanded, setIsFinanceCalculatorExpanded] = useState(false);
  const financeCalculatorRef = useRef(null);
  const [documentCount, setDocumentCount] = useState(0);
  const [checklistProgress, setChecklistProgress] = useState(0);
  const [checklistTotal, setChecklistTotal] = useState(0);
  // Only the TYPED delivery name lives here ('' = automatic). The automatic
  // name is the server's (formData.automatic_name, offer/deal_names.py).
  const [customDisplayName, setCustomDisplayName] = useState('');
  const [initialChecklistData, setInitialChecklistData] = useState(null);
  const [currentChecklistData, setCurrentChecklistData] = useState(null);
  
  // Simple user interaction tracking - ONLY track actual user input
  const userHasInteracted = useRef(false);
  const isInitialLoad = useRef(true); // Track if we're still loading initial data
  // Fields the user changed since the current delivery started loading. Unlike
  // userHasInteracted this is NOT gated on isInitialLoad: edits made while the
  // mount fetch is still in flight must survive its setFormData.
  const touchedFieldsSinceLoad = useRef(new Set());
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    // Trade Ops flavour (Solids vs Fuels). Seeded from router state so the CREATE
    // routes render the right form variant (BDN labels, no quality table for fuels)
    // from the first paint; for existing deliveries the API load overwrites this
    // with the delivery's own authoritative value.
    commodity_type: stateCommodityType,

    // System and Tracking Information
    delivery_unique_number: '',
    creation_timestamp: '',
    employee_user_identifier: '',
    associated_trade_recap_numbers: { trade_recap_numbers: [] },

    // Vessel Information
    vessel_name: '',
    vessel_imo_number: '',
    vessel_position: '',
    owners: '',
    charterers: '',
    brokers: '',
    
    // Vessel Parties (CRM-linked)
    vessel_owners: '',
    vessel_owners_crm_id: null,
    vessel_brokers: '',
    vessel_brokers_crm_id: null,

    // Delivery Information
    hms_entity: '',
    delivery_main_operator: '',
    delivery_backup_operator_1: '',
    delivery_backup_operator_2: '',
    delivery_assigned_hms_office: '',
    delivery_employee_list_viewing_access: [],
    delivery_employee_list_viewing_access_financials: [],
    delivery_deal_maker_sale: '',
    delivery_deal_maker_sale_percentage: '',
    delivery_deal_maker_purchase: '',
    delivery_deal_maker_purchase_percentage: '',
    delivery_deal_maker_freight: '',

    // Financial Information
    purchase_price_per_ton_contract: '',
    purchase_price_per_ton_contract_currency: '',
    purchase_price_per_ton_adjusted: '',
    purchase_price_per_ton_adjusted_currency: '',
    sales_price_per_ton_contract: '',
    sale_price_per_ton_contract_currency: '',
    sales_price_per_ton_adjusted: '',
    sale_price_per_ton_adjusted_currency: '',
    price_basis_purchase_amount: '',
    price_basis_purchase_amount_currency: '',
    price_basis_sale_amount: '',
    price_basis_sale_amount_currency: '',
    price_basis_purchase_quality: '',
    price_basis_sale_quality: '',

    // Product Details (Purchase)
    delivery_product_quantity: '',
    delivery_product_unit: '', // Unit for delivery product quantity
    delivery_product_tolerance: '',
    delivery_product_category: '', // Product variant ID
    delivery_product_description: '', // Product display name
    delivery_product_details: null, // Full product object
    
    // Product Details (Sales)
    sales_product_category: '', // Sales product variant ID
    sales_product_description: '', // Sales product display name
    sales_product_details: null, // Full sales product object
    sales_product_quantity: '', // Sales product quantity
    sales_product_unit: '', // Unit for sales product quantity
    sales_tolerance: '', // Sales tolerance percentage
    
    // Price Basis Fields (WHAT Tab)
    purchase_price_basis_value: '', // Price basis value for purchase
    purchase_price_basis_type: '', // Price basis type (NAR/GAR) for purchase
    sales_price_basis_value: '', // Price basis value for sales
    sales_price_basis_type: '', // Price basis type (NAR/GAR) for sales
    
    // Total Price and Quantity Fields (WHAT Tab - v2.1)
    total_purchase_price_per_ton_adjusted: '', // Total adjusted purchase price per ton
    total_purchase_price_per_ton_adjusted_currency: '', // Currency for total adjusted purchase price
    total_sale_price_per_ton_adjusted: '', // Total adjusted sales price per ton
    total_sale_price_per_ton_adjusted_currency: '', // Currency for total adjusted sales price
    total_purchase_tolerance: '', // Total purchase tolerance percentage
    total_sales_tolerance: '', // Total sales tolerance percentage
    total_purchase_product_quantity: '', // Total purchase product quantity
    total_purchase_product_unit: '', // Unit for total purchase quantity
    total_sales_product_quantity: '', // Total sales product quantity
    total_sales_product_unit: '', // Unit for total sales quantity

    // Business Relationship Information
    delivery_producer: '',
    delivery_producer_reporting_as_tickbox: false,
    delivery_chain_in_between_purchase_1: '',
    delivery_chain_in_between_purchase_2: '',
    delivery_chain_in_between_reporting_as_tickbox_1: false,
    delivery_chain_in_between_reporting_as_tickbox_2: false,
    seller_contractual: '',
    seller_contractual_reporting_as_tickbox: false,
    buyer_contractual: '',
    buyer_contractual_reporting_as_tickbox: false,
    delivery_chain_in_between_sale_1: '',
    delivery_chain_in_between_sale_2: '',
    delivery_chain_in_between_sale_reporting_as_tickbox_1: false,
    delivery_chain_in_between_sale_reporting_as_tickbox_2: false,
    delivery_end_user: '',
    
    // Multiple contractual partners (JSONField arrays)
    seller_contractual_list: [],
    buyer_contractual_list: [],
    delivery_end_user_reporting_as_tickbox: false,

    industry: '',

    // Port And Freight
    delivery_transport_mode: '',
    loading_port_location: '',
    product_origin: '',
    delivery_destination: '',
    delivery_estimated_time_of_arrival: '',
    discharging_port_location: '',
    charter_party_date: '',
    delivery_laycan: '',
    incoterms_purchase: '',
    incoterms_sale: '',
    freight_per_ton: '',
    freight_per_ton_currency: '',
    freight_per_ton_unit: '', // Unit for freight rate
    address_commission: '',
    mercurius_commission: '',
    brokerage_commission: '',

    // B/L Information
    bill_of_lading_date: '',
    bill_of_lading_date_estimated_or_final: '',
    bill_of_lading_quantity: '',
    bill_of_lading_quantity_unit: '', // Unit for B/L quantity
    bill_of_lading_quantity_estimated_or_final: '',
    reporting_month: '',

    // Contracts/Legal
    purchase_contract_quality_basis: '',
    sales_contract_quality_basis: '',
    purchase_confirmation_date: '',
    sale_confirmation_date: '',
    seller_contract_number: '',
    buyer_contract_number: '',
    kyc_information: '',
    sanction_check: '',

    // Product Quality Measurements
    // Loading Port
    loading_port_actual_quality_net_calorific_value_NAR: '',
    loading_port_actual_quality_total_moisture_ARB: '',
    loading_port_actual_quality_inherent_moisture: '',
    loading_port_actual_quality_ash_content: '',
    loading_port_actual_quality_volatile_matter: '',
    loading_port_actual_quality_total_sulphur: '',
    loading_port_actual_quality_hard_grove_index_HGI: '',
    loading_port_actual_quality_gross_calorific_value_GAR: '',
    loading_port_actual_quality_custom_added_fields: {},

    // Discharging Port
    discharging_port_actual_quality_net_calorific_value_NAR: '',
    discharging_port_actual_quality_total_moisture_ARB: '',
    discharging_port_actual_quality_inherent_moisture: '',
    discharging_port_actual_quality_ash_content: '',
    discharging_port_actual_quality_volatile_matter: '',
    discharging_port_actual_quality_total_sulphur: '',
    discharging_port_actual_quality_hard_grove_index_HGI: '',
    discharging_port_actual_quality_gross_calorific_value_GAR: '',
    discharging_port_actual_quality_custom_added_fields: {},

    // HMS Quality
    loading_port_HMS_quality_net_calorific_value_NAR: '',
    loading_port_HMS_quality_total_moisture_ARB: '',
    loading_port_HMS_quality_inherent_moisture: '',
    loading_port_HMS_quality_ash_content: '',
    loading_port_HMS_quality_volatile_matter: '',
    loading_port_HMS_quality_total_sulphur: '',
    loading_port_HMS_quality_hard_grove_index_HGI: '',
    loading_port_HMS_quality_gross_calorific_value_GAR: '',
    loading_port_HMS_quality_custom_added_fields: {},

    // Banking Information
    hms_banking_crm_partner_name: '',
    hms_bank_1: '',
    hms_bank_2: '',
    hms_banks: [],  // NEW: Dynamic bank selection (JSONField array)
    seller_banking_crm_partner_name: '',
    seller_banking_crm_id: null,
    seller_bank_1: '',
    seller_bank_2: '',
    seller_banks: [],  // NEW: Dynamic bank selection (JSONField array)
    buyer_banking_crm_partner_name: '',
    buyer_banking_crm_id: null,
    buyer_bank_1: '',
    buyer_bank_2: '',
    buyer_banks: [],  // NEW: Dynamic bank selection (JSONField array)

    // Additional Information
    additional_notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Row 125: why an existing delivery didn't load. While set, the screen shows
  // RecordLoadProblem INSTEAD of the form. A failed load used to leave the
  // blank form on screen (a 404 jumped to "new delivery"), and its Save
  // overwrote the delivery with blanks.
  const [deliveryLoadProblem, setDeliveryLoadProblem] = useState(null);
  const [deliveryLoadAttempt, setDeliveryLoadAttempt] = useState(0);
  // Which delivery in the address has actually loaded; saving any other is refused.
  const [loadedDeliveryId, setLoadedDeliveryId] = useState(null);

  // Authoritative commodity flavour: prefer the loaded delivery's own commodity_type,
  // fall back to router state. Keeps "back" pointing at the right Trade Ops dash
  // (e.g. a bunker/fuels delivery returns to Bunkers, not the Cargo/Solids dash) even
  // after a hard reload where location.state has been dropped.
  // Row 127: formData.commodity_type is only the record's once it has loaded (before
  // that it is the seed); with neither, Back asks which desk this person can open.
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: loadedDeliveryId === deliveryId ? formData.commodity_type : null,
    hintCommodity: location.state?.commodityType,
  });
  const commodityType = tradeOpsDesk.commodity;


  // Toggle document panel.
  //
  // The paperwork pane is now a COLUMN beside the delivery, not a cover over
  // it, so it deliberately does NOT freeze the page. Locking the body scroll
  // was what made side-by-side impossible: the delivery was still on screen
  // but could not be scrolled, so you could never line a contract up against
  // the part of the delivery you were checking. The Checklist, Invoice and
  // Cashflow panels are still overlays and still lock the page, which is why
  // opening one of those closes this pane first.
  const toggleDocumentPanel = () => {
    if (isChecklistPanelOpen) closeChecklistPanel();
    if (isInvoicePanelOpen) closeInvoicePanel();
    setIsDocumentPanelOpen(!isDocumentPanelOpen);
    document.body.style.overflow = '';
  };

  const closeDocumentPanel = () => {
    setIsDocumentPanelOpen(false);
    document.body.style.overflow = '';
  };

  // Toggle checklist panel
  const toggleChecklistPanel = () => {
    if (isDocumentPanelOpen) closeDocumentPanel();
    if (isInvoicePanelOpen) closeInvoicePanel();
    setIsChecklistPanelOpen(!isChecklistPanelOpen);
    document.body.style.overflow = !isChecklistPanelOpen ? 'hidden' : '';
  };

  const closeChecklistPanel = () => {
    setIsChecklistPanelOpen(false);
    document.body.style.overflow = '';
  };

  // Toggle invoice panel
  const toggleInvoicePanel = () => {
    if (isDocumentPanelOpen) closeDocumentPanel();
    if (isChecklistPanelOpen) closeChecklistPanel();
    setIsInvoicePanelOpen(!isInvoicePanelOpen);
    document.body.style.overflow = !isInvoicePanelOpen ? 'hidden' : '';
  };

  const closeInvoicePanel = () => {
    setIsInvoicePanelOpen(false);
    document.body.style.overflow = '';
  };

  // Toggle Finance Calculator (modal overlay)
  const toggleFinanceCalculator = () => {
    if (isDocumentPanelOpen) closeDocumentPanel();
    if (isChecklistPanelOpen) closeChecklistPanel();
    if (isInvoicePanelOpen) closeInvoicePanel();

    const willExpand = !isFinanceCalculatorExpanded;
    setIsFinanceCalculatorExpanded(willExpand);
    document.body.style.overflow = willExpand ? 'hidden' : '';
  };

  const closeFinanceCalculator = () => {
    setIsFinanceCalculatorExpanded(false);
    document.body.style.overflow = '';
  };

  // Handle budget linking from the Finance Calculator
  const handleBudgetLink = async (budgetHeaderId) => {
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') return;
    try {
      await axiosInstance.patch(`/api/deliveries/${deliveryId}/`, { budget: budgetHeaderId });
      setFormData((prev) => ({ ...prev, budget: budgetHeaderId }));
    } catch (err) {
      console.error('Failed to link budget:', err);
      showError('Failed to link budget to delivery');
    }
  };

  // Handle checklist data changes
  const handleChecklistChange = (data) => {
    console.log('Checklist data updated:', data);
    setCurrentChecklistData(data);
    
    // Update progress stats based on the new data
    updateChecklistProgressStats(data);
  };
  
  // Update progress statistics based on checklist data
  const updateChecklistProgressStats = (data) => {
    if (!data) return;
    
    // Calculate progress stats - all items are now flat
    const allItems = [
      ...(data.preShipment || []),
      ...(data.afterLoading || []),
      ...(data.afterDischarge || [])
    ];
    
    const completed = allItems.filter(item => item.status === 'completed').length;
    const notApplicable = allItems.filter(item => item.status === 'not_applicable').length;
    const total = allItems.length;
    
    setChecklistProgress(completed + notApplicable);
    setChecklistTotal(total);
  };

  // Handle checklist progress update
  const handleChecklistProgressUpdate = (progress, total) => {
    setChecklistProgress(progress);
    setChecklistTotal(total);
  };

  // Handle document count update from the DocumentPanel component
  const handleDocumentCountUpdate = (count) => {
    setDocumentCount(count);
  };

  // Cleanup effect for body style
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch document count when the delivery ID changes
  useEffect(() => {
    const fetchDocumentCount = async () => {
      if (deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined') {
        try {
          // Try to fetch from API
          try {
            const response = await axiosInstance.get(`/api/delivery-documents/count/${deliveryId}/`);
            setDocumentCount(response.data.count);
          } catch (error) {
            // If API endpoint doesn't exist yet, initialize with 0 or mock data
            console.warn('Document count API endpoint not available yet');
            setDocumentCount(0);
          }
        } catch (error) {
          console.error('Error fetching document count:', error);
        }
      } else {
        // Reset document count for new deliveries
        setDocumentCount(0);
      }
    };

    fetchDocumentCount();
  }, [deliveryId]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch checklist data
  const fetchChecklistData = async () => {
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
      return null;
    }
    
    try {
      const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/checklist/`);
      console.log('Loaded checklist data from backend for delivery:', deliveryId);
      
      // Transform backend data structure to frontend structure
      const data = {
        preShipment: response.data.pre_shipment_data || [],
        afterLoading: response.data.after_loading_data || [],
        afterDischarge: response.data.after_discharge_data || []
      };
      
      return data;
    } catch (error) {
      console.error('Error fetching checklist data:', error);
      
      // Return null on error
      return null;
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Real input events carry a DOM element as target; programmatic callers
    // pass a plain {target: {name, value}}. Only the former is a user touch —
    // programmatic mount-time writes must stay overwritable by the fetch.
    const isUserEvent = e.target.nodeType === 1;
    
    // Log the checkbox state changes for debugging
    if (type === 'checkbox') {
      console.log(`Checkbox change: ${name} = ${checked}`);
    }
    
    // Ensure boolean values are properly recorded for checkboxes
    if (name.includes('reporting_as_tickbox')) {
      console.log(`Processing reporting checkbox: ${name} = ${checked}`);
    }
    
    // Special handling and logging for port fields
    if (name === 'loading_port_location' || name === 'discharging_port_location') {
      console.log(`Port field update - ${name}:`, value);
    }
    
    // DEBUG: Log location field changes
    if (name === 'product_origin' || name === 'delivery_destination' || name === 'delivery_laycan') {
      console.log(`📍 PARENT handleInputChange - ${name}:`, value);
    }
    
    setFormData(prevState => {
      const newValue = type === 'checkbox' ? checked : value;
      const oldValue = prevState[name];
      
      // Helper to check if values are effectively the same (treating undefined/null/empty as equivalent)
      const isEffectivelySame = (a, b) => {
        // Normalize values for comparison
        const normalize = (val) => {
          // Treat undefined, null, and empty string as null for comparison
          if (val === undefined || val === null || val === '') return null;
          // Convert string representations of booleans to actual booleans
          if (val === 'true') return true;
          if (val === 'false') return false;
          // Actual booleans stay as-is
          if (val === true) return true;
          if (val === false) return false;
          return val;
        };
        
        const normA = normalize(a);
        const normB = normalize(b);
        
        // Special case: treat null/false as equivalent (initialization of boolean fields)
        if ((normA === null || normA === false) && (normB === null || normB === false)) {
          return true;
        }
        
        // Special case: treat null/true as equivalent ONLY if one is null (initialization)
        // This handles fields that default to true
        if (normA === null && normB === true) {
          return true; // undefined -> true is initialization
        }
        if (normA === true && normB === null) {
          return true; // true -> undefined is initialization
        }
        
        return normA === normB;
      };
      
      // ONLY mark as changed if:
      // 1. We're past the initial load phase
      // 2. The value is actually different (not just initialization)
      if (!isEffectivelySame(oldValue, newValue)) {
        // Recorded even during initial load, so the mount refetch can't clobber it
        if (isUserEvent) {
          touchedFieldsSinceLoad.current.add(name);
        }
        if (!isInitialLoad.current) {
          userHasInteracted.current = true;
          console.log(`🔴 USER CHANGED ${name} from "${oldValue}" to "${newValue}"`);
        }
      }
      
      const newState = {
        ...prevState,
        [name]: newValue
      };
      
      // Verify the port was actually set in state
      if (name === 'loading_port_location' || name === 'discharging_port_location') {
        console.log(`Updated form state for ${name}:`, newState[name]);
      }
      
      // DEBUG: Verify location fields are set in state
      if (name === 'product_origin' || name === 'delivery_destination' || name === 'delivery_laycan') {
        console.log(`📍 PARENT formData updated - ${name}:`, newState[name]);
      }
      
      return newState;
    });
  };

  // Validate recap overlap for workflow
  const validateRecapOverlapForWorkflow = () => {
    // Trigger fresh recap calculation
    const totals = formRef.current?.calculateRecapTotals();
    
    if (!totals) {
      return { 
        valid: false, 
        message: 'Unable to validate recap ranges. Please try again.' 
      };
    }
    
    // Check if recaps exist (need both purchase and sales)
    const hasPurchaseRecaps = (formData.purchase_recaps_data && formData.purchase_recaps_data.length > 0) ||
                               (totals.purchase.total_purchase_product_quantity && 
                                parseFloat(totals.purchase.total_purchase_product_quantity) > 0);
    const hasSalesRecaps = (formData.sales_recaps_data && formData.sales_recaps_data.length > 0) ||
                            (totals.sales.total_sales_product_quantity && 
                             parseFloat(totals.sales.total_sales_product_quantity) > 0);
    
    if (!hasPurchaseRecaps || !hasSalesRecaps) {
      return { 
        valid: false, 
        message: 'Cannot workflow delivery without purchase and sales recaps. Please add recaps or save as draft.' 
      };
    }
    
    return { valid: true };
  };

  // Handle form submission
  const handleSubmit = async (e, submitForApproval = false) => {
    // A submit from a pop-up's own form inside this screen is not a Save.
    if (!isOwnSubmit(e)) {
      e.preventDefault();
      return false;
    }
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Debug reporting checkbox values before submission
      console.log('Submitting form with reporting checkbox values:');
      const reportingCheckboxes = Object.entries(formData)
        .filter(([key]) => key.includes('reporting_as_tickbox'))
        .map(([key, value]) => ({ [key]: value }));
      console.log(reportingCheckboxes);

      // CRITICAL FIX: Get complete form data including seller/buyer contractual lists from DeliveryForm
      const completeFormData = formRef.current?.getFormData ? formRef.current.getFormData() : formData;
      console.log('🔍 WHO TAB - Contractual lists being sent:', {
        seller_contractual_list: completeFormData.seller_contractual_list,
        buyer_contractual_list: completeFormData.buyer_contractual_list
      });

      // Process form data to handle empty date fields correctly
      const processedFormData = { ...completeFormData };
      
      // Only the name the person typed is sent ('' or null = automatic), never
      // the name on screen: the automatic one stays the server's to work out.
      processedFormData.delivery_display_name = customDisplayName || null;
      
      
      // Get port validation status from DeliveryForm
      if (!formRef.current?.validatePorts()) {
        showError('Please select valid ports from the dropdowns');
        setIsLoading(false);
        return;
      }
      
      // Validate recap overlap for workflow submissions
      if (submitForApproval) {
        const validation = validateRecapOverlapForWorkflow();
        if (!validation.valid) {
          showError(validation.message);
          setIsLoading(false);
          return;
        }
      }
      
      // CRITICAL FIX: Combine laycan start and end dates into a single delivery_laycan string
      if (processedFormData.laycan_start_date && processedFormData.laycan_end_date) {
        // Format: "DD MMM YYYY - DD MMM YYYY" (e.g., "15 Mar 2025 - 20 Mar 2025")
        const startDate = new Date(processedFormData.laycan_start_date);
        const endDate = new Date(processedFormData.laycan_end_date);
        
        const formatDate = (date) => {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const day = date.getDate();
          const month = months[date.getMonth()];
          const year = date.getFullYear();
          return `${day} ${month} ${year}`;
        };
        
        processedFormData.delivery_laycan = `${formatDate(startDate)} - ${formatDate(endDate)}`;
        console.log('📍 Combined laycan dates into delivery_laycan:', processedFormData.delivery_laycan);
      } else if (processedFormData.laycan_start_date || processedFormData.laycan_end_date) {
        // If only one date is provided, use that single date
        const singleDate = new Date(processedFormData.laycan_start_date || processedFormData.laycan_end_date);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = singleDate.getDate();
        const month = months[singleDate.getMonth()];
        const year = singleDate.getFullYear();
        processedFormData.delivery_laycan = `${day} ${month} ${year}`;
        console.log('📍 Single laycan date set to delivery_laycan:', processedFormData.delivery_laycan);
      }
      
      // Remove the separate laycan date fields as they're now combined
      delete processedFormData.laycan_start_date;
      delete processedFormData.laycan_end_date;
      
      // List of date fields that need special handling
      const dateFields = [
        'purchase_confirmation_date',
        'sale_confirmation_date',
        'delivery_estimated_time_of_arrival',
        'bill_of_lading_date',
        'charter_party_date'
      ];
      
      // Process each date field - if empty, set to null
      dateFields.forEach(field => {
        if (!processedFormData[field] || processedFormData[field].trim() === '') {
          processedFormData[field] = null;
        }
      });
      
      // CRITICAL FIX: Handle tolerance fields - backend now stores component fields separately
      // Clean up the main tolerance fields (delivery_product_tolerance and sales_tolerance)
      const toleranceFields = [
        'delivery_product_tolerance',
        'sales_tolerance',
        'total_purchase_tolerance',
        'total_sales_tolerance'
      ];
      
      toleranceFields.forEach(field => {
        if (processedFormData[field] === '' || processedFormData[field] === null || processedFormData[field] === undefined) {
          processedFormData[field] = null;
        } else if (typeof processedFormData[field] === 'string') {
          const value = processedFormData[field].trim();
          // Parse formatted strings like "5% to 10%" and extract upper bound
          if (value.includes(' to ')) {
            const parts = value.split(' to ');
            if (parts.length === 2) {
              const upper = parseFloat(parts[1].replace(/[^0-9.-]/g, ''));
              processedFormData[field] = !isNaN(upper) ? upper.toFixed(2) : null;
            } else {
              processedFormData[field] = null;
            }
          } else {
            // Simple format - just remove % or MT and extract number
            const cleanValue = value.replace(/[^0-9.-]/g, '').trim();
            processedFormData[field] = cleanValue === '' ? null : cleanValue;
          }
        }
      });
      
      // Clean up component fields - convert empty strings to null
      const componentFields = [
        'delivery_product_tolerance_lower_value',
        'delivery_product_tolerance_upper_value',
        'sales_tolerance_lower_value',
        'sales_tolerance_upper_value'
      ];
      
      componentFields.forEach(field => {
        if (processedFormData[field] === '' || processedFormData[field] === undefined) {
          processedFormData[field] = null;
        }
      });
      
      console.log('📍 Processed tolerance fields (component fields stored separately):', {
        delivery_product_tolerance: processedFormData.delivery_product_tolerance,
        delivery_product_tolerance_lower_value: processedFormData.delivery_product_tolerance_lower_value,
        delivery_product_tolerance_lower_unit: processedFormData.delivery_product_tolerance_lower_unit,
        delivery_product_tolerance_upper_value: processedFormData.delivery_product_tolerance_upper_value,
        delivery_product_tolerance_upper_unit: processedFormData.delivery_product_tolerance_upper_unit,
        sales_tolerance: processedFormData.sales_tolerance,
        sales_tolerance_lower_value: processedFormData.sales_tolerance_lower_value,
        sales_tolerance_lower_unit: processedFormData.sales_tolerance_lower_unit,
        sales_tolerance_upper_value: processedFormData.sales_tolerance_upper_value,
        sales_tolerance_upper_unit: processedFormData.sales_tolerance_upper_unit,
        total_purchase_tolerance: processedFormData.total_purchase_tolerance,
        total_sales_tolerance: processedFormData.total_sales_tolerance
      });
      
      console.log('Processed form data with null date fields:', 
        dateFields.reduce((acc, field) => ({ ...acc, [field]: processedFormData[field] }), {})
      );

      // DEBUG: Log the banking and location fields being sent
      console.log('🏦 Banking fields being sent:', {
        hms_banking_crm_partner_name: processedFormData.hms_banking_crm_partner_name,
        hms_bank_1: processedFormData.hms_bank_1,
        hms_bank_2: processedFormData.hms_bank_2,
        hms_banks: processedFormData.hms_banks,
        seller_banking_crm_partner_name: processedFormData.seller_banking_crm_partner_name,
        seller_banking_crm_id: processedFormData.seller_banking_crm_id,
        seller_bank_1: processedFormData.seller_bank_1,
        seller_bank_2: processedFormData.seller_bank_2,
        seller_banks: processedFormData.seller_banks,
        buyer_banking_crm_partner_name: processedFormData.buyer_banking_crm_partner_name,
        buyer_banking_crm_id: processedFormData.buyer_banking_crm_id,
        buyer_bank_1: processedFormData.buyer_bank_1,
        buyer_bank_2: processedFormData.buyer_bank_2,
        buyer_banks: processedFormData.buyer_banks,
      });
      console.log('📍 Location fields being sent:', {
        delivery_laycan: processedFormData.delivery_laycan,
        product_origin: processedFormData.product_origin,
        delivery_destination: processedFormData.delivery_destination,
      });
      
      // CRITICAL DEBUG: Log WHAT tab fields being sent
      console.log('📦 WHAT TAB - Product fields being sent:', {
        delivery_product_category: processedFormData.delivery_product_category,
        sales_product_category: processedFormData.sales_product_category,
        stock_item_variant_id: processedFormData.stock_item_variant_id,
        delivery_product_quantity: processedFormData.delivery_product_quantity,
        delivery_product_unit: processedFormData.delivery_product_unit,
        sales_product_quantity: processedFormData.sales_product_quantity,
        sales_product_unit: processedFormData.sales_product_unit,
      });
      console.log('💰 WHAT TAB - Price fields being sent:', {
        purchase_price_per_ton_contract: processedFormData.purchase_price_per_ton_contract,
        sales_price_per_ton_contract: processedFormData.sales_price_per_ton_contract,
        sale_price_per_ton_contract_currency: processedFormData.sale_price_per_ton_contract_currency,
        sales_price_per_ton_adjusted: processedFormData.sales_price_per_ton_adjusted,
        sale_price_per_ton_adjusted_currency: processedFormData.sale_price_per_ton_adjusted_currency,
      });
      console.log('📊 WHAT TAB - Quantity/Unit fields being sent:', {
        total_purchase_product_quantity: processedFormData.total_purchase_product_quantity,
        total_purchase_product_unit: processedFormData.total_purchase_product_unit,
        total_sales_product_quantity: processedFormData.total_sales_product_quantity,
        total_sales_product_unit: processedFormData.total_sales_product_unit,
        freight_per_ton_unit: processedFormData.freight_per_ton_unit,
      });
      console.log('🎯 WHAT TAB - Price basis fields being sent:', {
        purchase_price_basis_value: processedFormData.purchase_price_basis_value,
        purchase_price_basis_type: processedFormData.purchase_price_basis_type,
        sales_price_basis_value: processedFormData.sales_price_basis_value,
        sales_price_basis_type: processedFormData.sales_price_basis_type,
        sales_tolerance: processedFormData.sales_tolerance,
      });

      // Add submit_for_approval flag if applicable
      if (submitForApproval) {
        processedFormData.submit_for_approval = true;
        console.log('🚀 Submitting delivery for approval workflow');
      }

      let result;
      // Check if we're creating a new delivery or updating an existing one
      if (deliveryId === 'new' || deliveryId === 'undefined' || !deliveryId) {
        // Create blank recaps in database before saving delivery
        let createdRecaps = null;
        // OLD: Create blank recaps before saving (REMOVED - now happens after save via createAndLinkBlankRecaps)
        
        // Use create_direct endpoint for new deliveries
        console.log('Creating new delivery');
        result = await axiosInstance.post('/api/deliveries/create_direct/', processedFormData);
        if (submitForApproval) {
          showSuccess('Delivery created and submitted for review');
        } else {
          showSuccess('Delivery draft saved successfully');
        }
        
        // CRITICAL FIX: Update URL to prevent duplicate deliveries on subsequent saves
        if (result.data.id) {
          history.replace(`/deliveries/${result.data.id}`);
          console.log(`URL updated to /deliveries/${result.data.id} to prevent duplicates`);
        }
        
        // CRITICAL FIX: Update formData with the new delivery number so it appears in the header
        if (result.data.delivery_number) {
          setFormData(prevData => ({
            ...prevData,
            delivery_unique_number: result.data.delivery_number
          }));
          console.log(`Delivery number ${result.data.delivery_number} updated in form state`);
        }
        
        // Create and link blank recaps after delivery is saved
        if (result.data.id && formRef.current?.createAndLinkBlankRecaps) {
          try {
            console.log('Creating and linking blank recaps to new delivery...');
            const recapResults = await formRef.current.createAndLinkBlankRecaps(result.data.id);
            
            if (recapResults.success && recapResults.count > 0) {
              showSuccess(`${recapResults.count} blank recap(s) created and linked successfully`);
            } else if (recapResults.errors.length > 0) {
              // Say why, in the server's words (ledger 165: "Cannot create recap:
              // Choose a unit for the quantity."), not only how many.
              showError(blankRecapFailure(recapResults.errors), 10000);
            }
          } catch (recapError) {
            console.error('Error creating blank recaps:', recapError);
            showError('Failed to create blank recaps');
          }
        }
        
        // Link pending linked recaps after delivery is saved
        if (result.data.id && formRef.current?.linkPendingLinkedRecaps) {
          try {
            console.log('Linking pending linked recaps to new delivery...');
            const linkResults = await formRef.current.linkPendingLinkedRecaps(result.data.id);
            
            if (linkResults.success && linkResults.count > 0) {
              showSuccess(`${linkResults.count} saved recap(s) linked successfully`);
            } else if (linkResults.errors.length > 0) {
              showError(`Failed to link ${linkResults.errors.length} saved recap(s)`);
            }
          } catch (linkError) {
            console.error('Error linking pending recaps:', linkError);
            showError('Failed to link saved recaps');
          }
        }
        
        // Reset user interaction flag after successful save
        userHasInteracted.current = false;
        touchedFieldsSinceLoad.current = new Set();
        
        if (result.data.id) {
          try {
            // Temporarily mark as initial load to prevent reload from triggering change detection
            isInitialLoad.current = true;
            
            console.log('Reloading delivery data after recap operations...');
            const deliveryResponse = await axiosInstance.get(`/api/deliveries/${result.data.id}/`);
            setFormData(deliveryResponse.data);
            
            // Re-enable change tracking after reload settles
            setTimeout(() => {
              isInitialLoad.current = false;
            }, 500);
          } catch (reloadError) {
            console.error('Error reloading delivery:', reloadError);
            // Still re-enable tracking even on error
            setTimeout(() => {
              isInitialLoad.current = false;
            }, 500);
          }
        }
        
        // Save checklist data if we have any and we now have a valid delivery ID
        if (currentChecklistData && result.data.id) {
          try {
            await axiosInstance.put(`/api/deliveries/${result.data.id}/checklist/`, {
              pre_shipment_data: currentChecklistData.preShipment || [],
              after_loading_data: currentChecklistData.afterLoading || [],
              after_discharge_data: currentChecklistData.afterDischarge || []
            });
            console.log('Saved checklist data for new delivery');
            
            // Update initial data after successful save
            setInitialChecklistData(currentChecklistData);
          } catch (checklistError) {
            console.error('Error saving checklist for new delivery:', checklistError);
          }
        }
      } else {
        // Make sure deliveryId is a valid ID before attempting to update
        if (deliveryId === 'undefined') {
          throw new Error('Invalid delivery ID. Please create a new delivery instead.');
        }

        // Row 125: the form is blank until this delivery loads; saving it
        // would overwrite the delivery with blanks.
        if (loadedDeliveryId !== deliveryId) {
          showError("This delivery hasn't loaded, so it can't be saved. Reload the page and try again.");
          return;
        }
        
        // Update the delivery first with regular field changes
        console.log(`Updating existing delivery with ID: ${deliveryId}`);
        result = await axiosInstance.put(`/api/deliveries/${deliveryId}/`, processedFormData);
        
        // THEN create and link blank recaps after the main update
        if (formRef.current?.createAndLinkBlankRecaps) {
          try {
            console.log('Creating and linking blank recaps for existing delivery...');
            const recapResults = await formRef.current.createAndLinkBlankRecaps(deliveryId);
            
            if (recapResults.success && recapResults.count > 0) {
              console.log(`${recapResults.count} blank recap(s) created and linked successfully`);
              showSuccess(`${recapResults.count} blank recap(s) created and linked successfully`);
            } else if (recapResults.errors.length > 0) {
              // Say why, in the server's words (ledger 165: "Cannot create recap:
              // Choose a unit for the quantity."), not only how many.
              showError(blankRecapFailure(recapResults.errors), 10000);
            }
          } catch (error) {
            console.error('Error creating blank recaps:', error);
            showError('Failed to create blank recaps');
          }
        }
        
        // Link pending linked recaps after the main update
        if (formRef.current?.linkPendingLinkedRecaps) {
          try {
            console.log('Linking pending linked recaps for existing delivery...');
            const linkResults = await formRef.current.linkPendingLinkedRecaps(deliveryId);
            
            if (linkResults.success && linkResults.count > 0) {
              console.log(`${linkResults.count} saved recap(s) linked successfully`);
            } else if (linkResults.errors.length > 0) {
              showError(`Failed to link ${linkResults.errors.length} saved recap(s)`);
            }
          } catch (error) {
            console.error('Error linking pending recaps:', error);
            showError('Failed to link saved recaps');
          }
        }
        
        // Reset user interaction flag after successful save
        userHasInteracted.current = false;
        touchedFieldsSinceLoad.current = new Set();
        
        // CRITICAL FIX: Reload delivery data to ensure form displays the latest saved values
        try {
          // Temporarily mark as initial load to prevent reload from triggering change detection
          isInitialLoad.current = true;
          
          console.log('Reloading delivery data after update to refresh form display...');
          const deliveryResponse = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
          setFormData(deliveryResponse.data);
          console.log('Form data updated with latest values from database');
          
          // Re-enable change tracking after reload settles
          setTimeout(() => {
            isInitialLoad.current = false;
          }, 500);
        } catch (reloadError) {
          console.error('Error reloading delivery after update:', reloadError);
          // Still re-enable tracking even on error
          setTimeout(() => {
            isInitialLoad.current = false;
          }, 500);
        }
        
        // Save checklist data if we have any changes
        if (currentChecklistData) {
          try {
            await axiosInstance.put(`/api/deliveries/${deliveryId}/checklist/`, {
              pre_shipment_data: currentChecklistData.preShipment || [],
              after_loading_data: currentChecklistData.afterLoading || [],
              after_discharge_data: currentChecklistData.afterDischarge || []
            });
            console.log('Saved checklist changes to backend');
            
            // Update initial data after successful save
            setInitialChecklistData(currentChecklistData);
          } catch (putError) {
            // If PUT fails, try POST as fallback
            try {
              console.log('PUT request failed, trying POST instead');
              await axiosInstance.post(`/api/deliveries/${deliveryId}/checklist/`, {
                pre_shipment_data: currentChecklistData.preShipment || [],
                after_loading_data: currentChecklistData.afterLoading || [],
                after_discharge_data: currentChecklistData.afterDischarge || []
              });
              console.log('Saved checklist changes to backend using POST');
              
              // Update initial data after successful save
              setInitialChecklistData(currentChecklistData);
            } catch (postError) {
              console.error('Error saving checklist to backend:', postError);
              // Continue with success message as delivery was saved successfully
            }
          }
        }
        
        if (submitForApproval) {
          showSuccess('Delivery updated and submitted for review');
        } else {
          showSuccess('Delivery draft updated successfully');
        }
      }
      
    } catch (error) {
      console.error('Error saving delivery:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message ||
                          'An error occurred while saving the delivery';
      
      showError(errorMessage);
      setError(errorMessage); // Keep for inline display
    } finally {
      setIsLoading(false);
    }
  };

  // Redirect to new delivery form if ID is undefined
  useEffect(() => {
    if (deliveryId === 'undefined') {
      console.log('Redirecting from undefined ID to new delivery form');
      history.replace('/deliveries/new');
    }
  }, [deliveryId, history]);

  // Fetch delivery data if editing
  useEffect(() => {
    setDeliveryLoadProblem(null);

    const fetchDelivery = async () => {
      if (deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined') {
        setIsLoading(true);
        touchedFieldsSinceLoad.current = new Set();
        try {
          // Fetch delivery data
          const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
          
          // Log reporting checkbox values from backend
          const reportingCheckboxes = Object.entries(response.data)
            .filter(([key]) => key.includes('reporting_as_tickbox'))
            .map(([key, value]) => ({ [key]: value }));
          console.log('Received reporting checkbox values from backend:', reportingCheckboxes);
          
          // This delivery's typed name, or none: never the last delivery's.
          setCustomDisplayName(response.data.delivery_display_name || '');
          
          // Log any custom quality fields
          const customQualityFields = Object.entries(response.data)
            .filter(([key]) => key.startsWith('custom_quality_'))
            .reduce((obj, [key, value]) => {
              obj[key] = value;
              return obj;
            }, {});
          
          if (Object.keys(customQualityFields).length > 0) {
            console.log('Received custom quality fields from backend:', customQualityFields);
          } else {
            console.log('No custom quality fields received from backend');
          }
          
          // DEBUG: Log banking and location fields received from backend
          console.log('🏦 Banking fields received from backend:', {
            hms_banking_crm_partner_name: response.data.hms_banking_crm_partner_name,
            hms_bank_1: response.data.hms_bank_1,
            hms_bank_2: response.data.hms_bank_2,
            hms_banks: response.data.hms_banks,
            seller_banking_crm_partner_name: response.data.seller_banking_crm_partner_name,
            seller_banking_crm_id: response.data.seller_banking_crm_id,
            seller_bank_1: response.data.seller_bank_1,
            seller_bank_2: response.data.seller_bank_2,
            seller_banks: response.data.seller_banks,
            buyer_banking_crm_partner_name: response.data.buyer_banking_crm_partner_name,
            buyer_banking_crm_id: response.data.buyer_banking_crm_id,
            buyer_bank_1: response.data.buyer_bank_1,
            buyer_bank_2: response.data.buyer_bank_2,
            buyer_banks: response.data.buyer_banks,
          });
          console.log('📍 Location fields received from backend:', {
            delivery_laycan: response.data.delivery_laycan,
            product_origin: response.data.product_origin,
            delivery_destination: response.data.delivery_destination,
          });
          
          // CRITICAL DEBUG: Log WHAT tab product fields received from backend
          console.log('📦 PRODUCT FIELDS received from backend:', {
            delivery_product_category: response.data.delivery_product_category,
            sales_product_category: response.data.sales_product_category,
            delivery_product_unit: response.data.delivery_product_unit,
            sales_product_unit: response.data.sales_product_unit,
            total_purchase_product_unit: response.data.total_purchase_product_unit,
            total_sales_product_unit: response.data.total_sales_product_unit,
          });
          
          // CRITICAL FIX: Split delivery_laycan string into separate start and end dates for the form
          const deliveryData = { ...response.data };
          if (deliveryData.delivery_laycan) {
            // Parse laycan string (e.g., "15 Mar 2025 - 20 Mar 2025")
            const laycanParts = deliveryData.delivery_laycan.split('-').map(part => part.trim());
            
            if (laycanParts.length === 2) {
              // Convert to ISO date format (YYYY-MM-DD) for date inputs
              const parseDate = (dateStr) => {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const parts = dateStr.trim().split(' ');
                if (parts.length === 3) {
                  const day = parts[0].padStart(2, '0');
                  const monthIndex = months.indexOf(parts[1]);
                  const month = (monthIndex + 1).toString().padStart(2, '0');
                  const year = parts[2];
                  return `${year}-${month}-${day}`;
                }
                return null;
              };
              
              deliveryData.laycan_start_date = parseDate(laycanParts[0]);
              deliveryData.laycan_end_date = parseDate(laycanParts[1]);
              console.log('📍 Split laycan into:', {
                start: deliveryData.laycan_start_date,
                end: deliveryData.laycan_end_date
              });
            } else if (laycanParts.length === 1) {
              // Single date
              const parseDate = (dateStr) => {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const parts = dateStr.trim().split(' ');
                if (parts.length === 3) {
                  const day = parts[0].padStart(2, '0');
                  const monthIndex = months.indexOf(parts[1]);
                  const month = (monthIndex + 1).toString().padStart(2, '0');
                  const year = parts[2];
                  return `${year}-${month}-${day}`;
                }
                return null;
              };
              
              deliveryData.laycan_start_date = parseDate(laycanParts[0]);
              deliveryData.laycan_end_date = parseDate(laycanParts[0]);
            }
          }
          
          // CRITICAL FIX: Extract product from active recap when loading delivery
          // ALWAYS populate from recap if recap has product (recap is source of truth)
          if (deliveryData.purchase_recaps_data && deliveryData.active_purchase_recap_id) {
            const activeRecap = deliveryData.purchase_recaps_data.find(
              r => r.id === deliveryData.active_purchase_recap_id
            );
            if (activeRecap && activeRecap.product) {
              deliveryData.product_name = activeRecap.product;
              console.log('📦 Populated product_name from active purchase recap:', activeRecap.product);
            }
          } else if (deliveryData.sales_recaps_data && deliveryData.active_sales_recap_id) {
            const activeRecap = deliveryData.sales_recaps_data.find(
              r => r.id === deliveryData.active_sales_recap_id
            );
            if (activeRecap && activeRecap.product) {
              deliveryData.product_name = activeRecap.product;
              console.log('📦 Populated product_name from active sales recap:', activeRecap.product);
            }
          }
          
          // Keep any field the user already edited while this fetch was in
          // flight — a wholesale replace silently reverts those edits.
          setFormData(prevData => {
            const touched = touchedFieldsSinceLoad.current;
            if (touched.size === 0) return deliveryData;
            const merged = { ...deliveryData };
            touched.forEach(name => {
              merged[name] = prevData[name];
            });
            console.log('⚠️ Preserved user-edited fields over late fetch:', [...touched]);
            return merged;
          });
          setLoadedDeliveryId(deliveryId);

          // Now fetch checklist data
          const checklistData = await fetchChecklistData();
          if (checklistData) {
            setInitialChecklistData(checklistData);
            setCurrentChecklistData(checklistData);
            
            // Calculate initial progress
            updateChecklistProgressStats(checklistData);
          }
        } catch (error) {
          console.error('Error fetching delivery:', error);
          // Keep the answer: a refused or missing delivery shows the message,
          // never the blank form (and never a jump to "new delivery").
          setDeliveryLoadProblem(describeLoadFailure(error));
        } finally {
          setIsLoading(false);
          // Mark initial load as complete after a small delay to allow all state updates to settle
          setTimeout(() => {
            isInitialLoad.current = false;
          }, 500);
        }
      } else if (deliveryId === 'new' || deliveryId === 'undefined') {
        // Mark initial load as complete for new deliveries after a small delay
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 500);
      }
    };

    fetchDelivery();
  }, [deliveryId, history, deliveryLoadAttempt]);

  // Store original form data after loading delivery

  // Add beforeunload handler for browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (userHasInteracted.current) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Navigation helpers
  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const isDashboardActive = () => {
    return location.pathname.startsWith('/dashboard') ? "active" : "";
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    history.push('/login');
  };

  const handleBackClick = () => {
    if (isDocumentPanelOpen) closeDocumentPanel();
    if (isChecklistPanelOpen) closeChecklistPanel();
    if (isInvoicePanelOpen) closeInvoicePanel();

    if (userHasInteracted.current) {
      setShowBackConfirmation(true);
    } else {
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'deliveries',
      });
    }
  };

  const handleConfirmBack = () => {
    userHasInteracted.current = false;
    setShowBackConfirmation(false);
    goToTradeOps(history, tradeOpsDesk, {
      selectedDate: location.state?.selectedDate,
      activeTab: location.state?.activeTab || 'deliveries',
    });
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
  };

  const handleDraftAndLeave = async () => {
    try {
      // Save as draft using existing handleSubmit with submitForApproval = false
      const fakeEvent = { preventDefault: () => {} };
      await handleSubmit(fakeEvent, false);
      
      // The handleSubmit function properly resets hasUnsavedChanges to false
      // Close modal and navigate away
      setShowBackConfirmation(false);
      
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'deliveries',
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft. Please try again.');
    }
  };

  if (deliveryLoadProblem) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="delivery-screen">
          <RecordLoadProblem
            problem={deliveryLoadProblem}
            recordName="delivery"
            areaName="deliveries"
            backLabel="Back to Trade Ops"
            onBack={handleConfirmBack}
            onRetry={() => setDeliveryLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="delivery-screen">
        {/* HMS Screen Header */}
        <div className="hms-screen-header">
          {/* Back Button */}
          <button 
            className="delivery-header-back-button"
            onClick={handleBackClick}
            title="Back to Trade Operations"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="header-logo">
            <img src={logo} alt="IMT" className="header-logo-img" />
            <div className="header-title-content">
              <div className="delivery-header-stacked-fields">
                <div className="delivery-header-name-row">
                  <HeaderNameEditor
                    typedName={customDisplayName}
                    automaticName={formData.automatic_name}
                    onTypedNameChange={(name) => {
                      setCustomDisplayName(name);
                      userHasInteracted.current = true;
                    }}
                    emptyLabel="Custom delivery name..."
                    inputClassName="delivery-header-name-input"
                    displayClassName="delivery-header-name-display"
                    iconClassName="delivery-header-edit-icon"
                  />
                </div>
                <div className="delivery-header-info-row">
                  <span className="delivery-header-vessel-display">
                    Vessel: {formData.vessel_name || '- -'}
                  </span>
                  <span className="delivery-header-number-display">
                    Delivery: {formData.delivery_unique_number || 'Auto Generated'}
                  </span>
                  {formData.creation_timestamp && (
                    <CreatedByLine
                      className="delivery-header-created-by"
                      name={formData.created_by_name}
                      when={formData.creation_timestamp}
                    />
                  )}
                  {formData.status && (
                    <span className={`delivery-status-badge delivery-status-${formData.status?.toLowerCase().replace('_', '-')}`}>
                      {formData.status === 'DRAFT' && <><FontAwesomeIcon icon={faSave} /> Draft</>}
                      {formData.status === 'PENDING_REVIEW' && <><FontAwesomeIcon icon={faHourglassHalf} /> Pending Review</>}
                      {formData.status === 'REVIEWED' && <><FontAwesomeIcon icon={faCheckCircle} /> Reviewed</>}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="header-actions" style={{ display: 'flex', gap: '12px', position: 'relative' }}>
            <PinToDeskButton entityType="delivery" objectId={deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined' ? deliveryId : null} />
            <button 
              className="btn-secondary"
              onClick={(e) => handleSubmit(e, false)}
              disabled={isLoading}
              style={{ minWidth: '120px' }}
            >
              <FontAwesomeIcon icon={faSave} /> Save Draft
            </button>
            <button 
              className="btn-primary"
              onClick={(e) => handleSubmit(e, true)}
              disabled={isLoading}
              style={{ minWidth: '160px' }}
            >
              <FontAwesomeIcon icon={faPaperPlane} /> Save & Workflow
            </button>
            
            {/* Action Button with Menu - Integrated into Header */}
            <ActionButton
              documentCount={documentCount}
              checklistProgress={checklistProgress}
              checklistTotal={checklistTotal}
              onDocumentClick={toggleDocumentPanel}
              onChecklistClick={toggleChecklistPanel}
              onInvoiceClick={toggleInvoicePanel}
              onCashflowClick={toggleFinanceCalculator}
              onLogsClick={() => setIsLogsModalOpen(true)}
            />
          </div>
        </div>

        {/* Checklist Panel */}
        <ChecklistPanel 
          isOpen={isChecklistPanelOpen}
          onClose={closeChecklistPanel}
          deliveryId={deliveryId}
          showSuccess={showSuccess}
          showError={showError}
          onProgressUpdate={handleChecklistProgressUpdate}
          initialChecklistData={currentChecklistData}
          onChecklistChange={handleChecklistChange}
        />

        {/* Invoice Panel */}
        <InvoicePanel 
          isOpen={isInvoicePanelOpen}
          onClose={closeInvoicePanel}
          deliveryId={deliveryId}
          deliveryStatus={formData.status}
        />

        {/* [ Delivery | Contracts & Notes ] -- Marcel, Sept feedback.
            The pane is a real second column, so the delivery keeps scrolling
            underneath it and the two can be read against each other. With the
            pane closed the grid collapses to one column and the delivery is
            exactly as wide as it was before. */}
        <div className={`delivery-split${isDocumentPanelOpen ? ' delivery-split--open' : ''}`}>
          <div className="delivery-content">
            <form onSubmit={handleSubmit} className="delivery-form">
              <DeliveryForm 
                formData={formData}
                handleInputChange={handleInputChange}
                markAsChanged={() => {
                  userHasInteracted.current = true;
                }}
                ref={formRef}
                deliveryId={deliveryId}
              />
            </form>
          </div>

          <DocumentsSplitPane
            isOpen={isDocumentPanelOpen}
            onClose={closeDocumentPanel}
            title="Contracts & Notes"
          >
            <DocumentPanel
              docked
              isOpen={isDocumentPanelOpen}
              onClose={closeDocumentPanel}
              deliveryId={deliveryId}
              commodityType={formData.commodity_type || commodityType}
              buyerPartnerName={formData.buyer_crm_profile_name || ''}
              sellerPartnerName={formData.seller_crm_profile_name || ''}
              showSuccess={showSuccess}
              showError={showError}
              onDocumentCountChange={handleDocumentCountUpdate}
            />
          </DocumentsSplitPane>
        </div>

        {/* Finance Calculator -- full-screen modal */}
        <FinanceCalculator
          ref={financeCalculatorRef}
          deliveryId={deliveryId}
          budgetId={formData.budget || null}
          isExpanded={isFinanceCalculatorExpanded}
          isModal={true}
          onClose={closeFinanceCalculator}
          onToggle={toggleFinanceCalculator}
          showSuccess={showSuccess}
          showError={showError}
          onBudgetLink={handleBudgetLink}
          purchaseQuantity={formData.delivery_product_quantity || formData.total_purchase_product_quantity}
          purchaseUnit={formData.delivery_product_unit || formData.total_purchase_product_unit || 'MT'}
          saleQuantity={formData.sales_product_quantity || formData.total_sales_product_quantity}
          saleUnit={formData.sales_product_unit || formData.total_sales_product_unit || 'MT'}
        />
        
        {/* Hamster Chat Integration */}
        <HamsterFloatingButton hidden={isFinanceCalculatorExpanded} />
        
        {/* Back to Top Button */}
        <BackToTopButton />

        {/* HMS Footer */}
        <Footer />
        
        {/* Delivery Logs Modal */}
        <DeliveryLogsModal
          isOpen={isLogsModalOpen}
          onClose={() => setIsLogsModalOpen(false)}
          deliveryId={deliveryId}
          deliveryNumber={formData.delivery_unique_number}
        />
        
        {/* Unsaved Changes Confirmation Modal */}
        {showBackConfirmation && (
          <DeliveryBackModal
            message="Unsaved delivery data will be lost. Are you sure you want to go back?"
            onConfirm={handleConfirmBack}
            onCancel={handleCancelBack}
            onDraft={handleDraftAndLeave}
          />
        )}
      </div>
    </Layout>
  );
}

export default withAuth(DeliveryScreen); 